/**
 * sl-suoi.1.3: Hot Sync Initialization Service
 *
 * Injects priority doc IDs into the sync engine after workspace initialization.
 * For cloud workspaces, the sync engine starts in selective mode (no initial docs).
 * This service adds Recent 5 docs as high-priority items; a workspace without
 * recent docs starts with its tutorial doc instead. Once the doc list is
 * ready, the image and attachment blobs of these hot docs are downloaded into
 * the local blob cache, one request at a time.
 *
 * SOLID:
 * - S: Only handles initial hot doc injection and warming
 * - D: Depends on abstractions (WorkspaceService, RecentDocsService, DocsService)
 */
import { DebugLogger } from '@affine/debug';
import { OnEvent, Service } from '@toeverything/infra';
import { filter, firstValueFrom } from 'rxjs';

import { DocsService } from '../../doc';
import { RecentDocsService } from '../../quicksearch';
import {
  type Workspace,
  WorkspaceInitialized,
  WorkspaceService,
} from '../../workspace';

const logger = new DebugLogger('HotSyncInit');

const HOT_DOC_PRIORITY = 10;
const HOT_DOC_ASSET_FLAVOURS = ['affine:image', 'affine:attachment'];

@OnEvent(WorkspaceInitialized, s => s.onWorkspaceInitialized)
export class HotSyncInitService extends Service {
  private disposed = false;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly recentDocsService: RecentDocsService,
    private readonly docsService: DocsService
  ) {
    super();
  }

  onWorkspaceInitialized(_workspace: Workspace): void {
    const workspace = this.workspaceService.workspace;
    if (workspace.flavour === 'local') {
      return; // Local workspaces use eager sync
    }

    const recentDocIds = this.recentDocsService.getRecentDocIds();

    logger.info(`Injecting ${recentDocIds.length} recent docs into hot sync`);

    // Add recent docs with high priority so they sync first
    recentDocIds.forEach(docId => this.addHotDoc(docId));

    // The currently open doc will be added by the workbench when it opens.
    this.warmHotDocs(recentDocIds).catch(error => {
      // Warming is optional: unwarmed docs load their assets on open.
      logger.warn('Hot doc warming failed', error);
    });
  }

  override dispose(): void {
    this.disposed = true;
    super.dispose();
  }

  private addHotDoc(docId: string) {
    const engine = this.workspaceService.workspace.engine;
    engine.doc.addDoc(docId, 'hot-sync-recent');
    engine.doc.addPriority(docId, HOT_DOC_PRIORITY);
  }

  private async warmHotDocs(recentDocIds: readonly string[]) {
    const list = this.docsService.list;
    await firstValueFrom(list.isReady$.pipe(filter(Boolean)));
    if (this.disposed) return;

    const hotDocIds = [...recentDocIds];
    if (hotDocIds.length === 0) {
      // A new workspace has no history yet; its users open the tutorial doc.
      // A deleted tutorial doc simply is not part of the list anymore.
      for (const record of list.demoDocs$.value) {
        if (record.trash$.value) continue;
        this.addHotDoc(record.id);
        hotDocIds.push(record.id);
      }
    }

    for (const docId of hotDocIds) {
      if (this.disposed) return;
      const record = list.doc$(docId).value;
      if (!record || record.trash$.value) continue;
      for (const blobId of await this.collectAssetBlobIds(docId)) {
        if (this.disposed) return;
        await this.workspaceService.workspace.engine.blob.get(blobId);
      }
    }
  }

  private async collectAssetBlobIds(docId: string): Promise<string[]> {
    const { doc, release } = this.docsService.open(docId);
    try {
      await doc.waitForSyncReady();
      const blobIds = new Set<string>();
      for (const model of doc.blockSuiteDoc.getModelsByFlavour(
        HOT_DOC_ASSET_FLAVOURS
      )) {
        const sourceId = (model.props as { sourceId?: unknown }).sourceId;
        if (typeof sourceId === 'string' && sourceId) blobIds.add(sourceId);
      }
      return [...blobIds];
    } finally {
      release();
    }
  }
}
